import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, Send, ArrowRight, RotateCcw, User as UserIcon } from "lucide-react";
import { sendSemMessage, resetSemSession } from "@/lib/sem";
import { useServiceI18n } from "@/lib/serviceI18n";

const INITIAL = {
  nl: "Hoi! Ik ben SEM. Beschrijf jouw situatie in één zin — bijvoorbeeld 'ZZP'er net begonnen, ik snap niets van BTW' — en ik vertel je welke dienstverlener je nodig hebt.",
  en: "Hi! I'm SEM. Describe your situation in one sentence — for example 'Just started as a freelancer, lost on VAT' — and I'll tell you which service provider you need.",
  de: "Hi! Ich bin SEM. Beschreibe deine Situation in einem Satz — zum Beispiel 'Frisch selbstständig, kein Plan von MwSt.' — und ich sage dir, welchen Dienstleister du brauchst.",
  fr: "Salut ! Je suis SEM. Décris ta situation en une phrase — par exemple 'Indépendant débutant, perdu avec la TVA' — et je te dirai quel prestataire il te faut.",
  es: "¡Hola! Soy SEM. Describe tu situación en una frase — por ejemplo 'Autónomo recién empezando, perdido con el IVA' — y te diré qué proveedor necesitas.",
  it: "Ciao! Sono SEM. Descrivi la tua situazione in una frase — ad esempio 'Appena partito come autonomo, perso con l'IVA' — e ti dirò di quale fornitore hai bisogno.",
  pt: "Oi! Sou o SEM. Descreva sua situação em uma frase — por exemplo 'Acabei de começar como autônomo, perdido com impostos' — e direi qual fornecedor você precisa.",
  pl: "Cześć! Jestem SEM. Opisz swoją sytuację jednym zdaniem — na przykład 'Świeży JDG, zagubiony w VAT' — a powiem ci jakiego dostawcy potrzebujesz.",
  tr: "Merhaba! Ben SEM. Durumunu tek cümleyle anlat — örneğin 'Yeni serbest çalışan, KDV'yi anlamıyorum' — ve sana hangi sağlayıcıya ihtiyacın olduğunu söyleyeyim.",
};

const SAMPLES = {
  nl: ["ZZP'er net begonnen, BTW snap ik niet", "Datalek bij ons gebeurd", "Wil mijn merk opnieuw lanceren", "Te kantoor verhuizen in Utrecht"],
  en: ["Just started as freelancer, lost on VAT", "We had a data breach", "Want to relaunch my brand", "Moving offices in Utrecht"],
  de: ["Frisch selbstständig, MwSt. unklar", "Datenleck bei uns passiert", "Marke neu launchen", "Büroumzug in Berlin"],
  fr: ["Indépendant débutant, perdu avec la TVA", "Fuite de données dans notre entreprise", "Relancer ma marque", "Déménagement bureau à Paris"],
  es: ["Autónomo recién empezando, perdido con el IVA", "Brecha de datos en mi empresa", "Quiero relanzar mi marca", "Mudanza de oficina en Madrid"],
  it: ["Appena partito come autonomo, perso con l'IVA", "Data breach in azienda", "Voglio rilanciare il mio brand", "Trasloco ufficio a Milano"],
  pt: ["Acabei de começar como autônomo, perdido com impostos", "Vazamento de dados na empresa", "Quero relançar minha marca", "Mudança de escritório em SP"],
  pl: ["Świeży JDG, zagubiony w VAT", "Wyciek danych w firmie", "Chcę zrelaunchować markę", "Przeprowadzka biura w Warszawie"],
  tr: ["Yeni serbest çalışan, KDV anlamıyorum", "Şirkette veri ihlali", "Markamı yeniden lanse etmek istiyorum", "İstanbul'da ofis taşıma"],
};

export const SemChat = ({ variant = "page", autoSubmit }) => {
  const { t, i18n } = useTranslation();
  const { subName, catName } = useServiceI18n();
  const lang = (i18n.language || "nl").slice(0, 2).toLowerCase();
  const initial = INITIAL[lang] || INITIAL.en;
  const samples = SAMPLES[lang] || SAMPLES.en;

  const [messages, setMessages] = useState([{ role: "assistant", text: initial }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    setMessages((m) => (m.length === 1 ? [{ role: "assistant", text: initial }] : m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (!autoSubmit || autoSubmittedRef.current) return;
    autoSubmittedRef.current = true;
    submit(autoSubmit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmit]);

  const submit = async (text) => {
    const userText = (text ?? input).trim();
    if (!userText || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: userText }]);
    setLoading(true);
    try {
      const res = await sendSemMessage({ message: userText, language: lang });
      setMessages((m) => [...m, { role: "assistant", text: res.reply, suggested: res.suggested || [], wizard_url: res.wizard_url }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: t("common.error_generic"), error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    resetSemSession();
    setMessages([{ role: "assistant", text: initial }]);
    setInput("");
  };

  const isCompact = variant === "compact" || variant === "floating";

  return (
    <div className={`flex flex-col ${isCompact ? "h-full" : "h-[68vh] max-h-[680px]"}`} data-testid="sem-chat">
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 md:px-6 py-6 space-y-4 bg-[#FDFCFB]"
      >
        {messages.map((m, i) => {
          const isUser = m.role === "user";
          return (
            <div key={i} className={`flex ${isUser ? "justify-end" : "justify-start"} gap-2`}>
              {!isUser && (
                <div className="w-8 h-8 rounded-md bg-[#0A1128] text-[#F5A623] flex items-center justify-center flex-shrink-0 text-[10px] font-extrabold tracking-wider">
                  SEM
                </div>
              )}
              <div className={`max-w-[85%] rounded-md px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap ${
                isUser
                  ? "bg-[#0A1128] text-white rounded-tr-sm"
                  : m.error
                    ? "bg-red-50 text-red-700 rounded-tl-sm border border-red-100"
                    : "bg-white text-[#0A1128] rounded-tl-sm border border-[#EAE6DF]"
              }`}>
                {m.text}
                {m.suggested?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#EAE6DF] space-y-2">
                    <div className="eyebrow">{t("sem.suggested_label")}</div>
                    {m.suggested.map((s) => (
                      <Link
                        key={s.id}
                        to={`/diensten/${s.cat_id}`}
                        className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-[#F3F0EA]/60 hover:bg-[#F5A623]/12 border border-[#EAE6DF] hover:border-[#F5A623]/40 transition-all group"
                        data-testid={`sem-suggested-${s.id}`}
                      >
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium text-[#0A1128] truncate">{subName({ id: s.id, naam: s.naam })}</div>
                          <div className="text-[10px] text-[#0A1128]/50 uppercase tracking-[0.14em] truncate mt-0.5">{catName({ id: s.cat_id, naam: s.categorie })}</div>
                        </div>
                        <ArrowRight size={14} className="text-[#F5A623] flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
                      </Link>
                    ))}
                    {m.wizard_url && (
                      <Link
                        to={m.wizard_url}
                        className="inline-flex items-center justify-center gap-2 w-full mt-2 px-4 py-2.5 rounded-md bg-[#F5A623] text-[#0A1128] text-[13px] font-semibold hover:bg-[#E0941A] transition-colors"
                        data-testid="sem-wizard-cta"
                      >
                        {t("sem.start_wizard")} <ArrowRight size={14} />
                      </Link>
                    )}
                  </div>
                )}
              </div>
              {isUser && (
                <div className="w-8 h-8 rounded-md bg-[#F3F0EA] text-[#0A1128]/60 flex items-center justify-center flex-shrink-0 border border-[#EAE6DF]">
                  <UserIcon size={14} />
                </div>
              )}
            </div>
          );
        })}
        {loading && (
          <div className="flex justify-start gap-2">
            <div className="w-8 h-8 rounded-md bg-[#0A1128] text-[#F5A623] flex items-center justify-center flex-shrink-0 text-[10px] font-extrabold tracking-wider">SEM</div>
            <div className="bg-white border border-[#EAE6DF] rounded-md rounded-tl-sm px-4 py-3">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-[#0A1128]/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-[#0A1128]/40 rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
                <span className="w-1.5 h-1.5 bg-[#0A1128]/40 rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
              </div>
            </div>
          </div>
        )}
        {messages.length === 1 && (
          <div className="pt-2">
            <div className="eyebrow mb-3">{t("sem.sample_questions")}</div>
            <div className="flex flex-wrap gap-2">
              {samples.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => submit(s)}
                  className="text-[12px] px-3 py-1.5 rounded-md border border-[#EAE6DF] bg-white hover:border-[#0A1128]/30 hover:bg-[#F3F0EA] text-[#0A1128]/75 hover:text-[#0A1128] transition-all"
                  data-testid="sem-sample"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        className="border-t border-[#EAE6DF] bg-white p-3 flex items-center gap-2"
      >
        <button
          type="button"
          onClick={reset}
          className="p-2 rounded-md text-[#0A1128]/40 hover:text-[#0A1128] hover:bg-[#F3F0EA] transition-colors"
          title={t("sem.reset")}
          data-testid="sem-reset"
        >
          <RotateCcw size={16} />
        </button>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("sem.input_placeholder")}
          className="flex-1 px-3 py-2.5 bg-[#F3F0EA]/50 rounded-md outline-none focus:bg-white focus:ring-2 focus:ring-[#F5A623]/40 text-[14px] border border-transparent focus:border-[#EAE6DF] transition-all placeholder:text-[#0A1128]/40"
          disabled={loading}
          data-testid="sem-input"
        />
        <button
          type="submit"
          disabled={!input.trim() || loading}
          className="p-2.5 rounded-md bg-[#F5A623] text-[#0A1128] hover:bg-[#E0941A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="sem-send"
          aria-label="Send"
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  );
};

export const SemHeader = ({ subtitle }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 px-5 py-4 border-b border-[#EAE6DF] bg-white">
      <div className="relative">
        <div className="w-11 h-11 rounded-md bg-[#0A1128] text-[#F5A623] flex items-center justify-center font-heading font-extrabold text-[11px] tracking-wider">SEM</div>
        <span className="absolute inset-0 rounded-md bc-pulse-ring pointer-events-none" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-medium text-[#0A1128] leading-tight">SEM</div>
        <div className="text-[11px] text-[#0A1128]/55 truncate mt-0.5">{subtitle || t("sem.tagline")}</div>
      </div>
      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#16A34A] flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] bc-live-dot" /> {t("sem.online")}
      </span>
    </div>
  );
};
